import type { SimpleExperiment } from "@/features/experiments/tabs/hooks/useExperimentDetails";
import type {
  AnalyticsQuery,
  RangeResult,
} from "schemas/generated/analyticsRead";
import type { Experiment } from "schemas/generated/experiment";

import * as React from "react";

import MetricWithDelta from "@editor/components/analytics/MetricWithDelta";
import { Loader } from "@editor/components/common/Loader";
import { useSubscriptionInfo } from "@editor/hooks/subscription";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { docs } from "@editor/utils/docs";
import { routes } from "@editor/utils/router";

import {
  DEFAULT_FILTERS,
  DEFAULT_QUERY,
  OVERVIEW_TABLE_METRICS,
} from "@/features/analytics/constants";
import useBasicAnalyticsRead from "@/features/analytics/useBasicAnalyticsRead";
import { ExperimentActionsBar } from "@/features/experiments/components/ExperimentActionsBar";
import { ExperimentsLayout } from "@/features/experiments/Layout";
import { ExperimentTabWrapper } from "@/features/experiments/tabs/ExperimentTabWrapper";
import { useExperimentDetails } from "@/features/experiments/tabs/hooks/useExperimentDetails";
import { successToast } from "@replo/design-system/components/alert/Toast";
import Button from "@replo/design-system/components/button/Button";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@replo/design-system/components/shadcn/core/table";
import { Tag } from "@replo/design-system/components/tag/Tag";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import { experimentStatusMapping } from "@replo/design-system/utils/experimentStatus";
import twMerge from "@replo/design-system/utils/twMerge";
import copy from "copy-to-clipboard";
import { endOfDay, startOfDay } from "date-fns";
import { ArrowUpRight, Copy, ExternalLink, Minus, Network } from "lucide-react";
import { generatePath, Link, useNavigate } from "react-router-dom";
import { convertMsToDays } from "replo-utils/lib/datetime";
import { formatWithCommasAndTwoDecimals } from "replo-utils/lib/math";
import {
  ConditionOperatorEnum,
  DEFAULT_MAIN_RANGE_ID,
  PageTypeEnum,
} from "schemas/analyticsRead";
import { BillingTiers } from "schemas/billing";
import { getExperimentStatus } from "schemas/experiment";

import { VariationNameStartEnhancer } from "../components/VariationNameStartEnhancer";
import { ExperimentEditableHeader } from "../ExperimentEditableHeader";
import { useExperimentApi } from "../utils";
import { useExperimentEdit } from "./hooks/useExperimentEdit";

type ValueContainerProps = {
  className?: string;
  children: React.ReactNode;
};

const ValuesContainer: React.FC<ValueContainerProps> = ({
  className,
  children,
}) => {
  return (
    <div
      className={twMerge(
        "border-0.5 border-border col-span-6 flex flex-row gap-8 rounded-md p-4",
        className,
      )}
    >
      {children}
    </div>
  );
};

type TitleWithValueProps = {
  title: string;
  value: string | null;
  valueAsChildren?: React.ReactNode;
};

const TitleWithValue: React.FC<TitleWithValueProps> = ({ title, value }) => {
  return (
    <div className="text-default flex flex-col gap-2">
      <span className="text-sm font-medium">{title}</span>
      <span className="text-sm">{value !== null ? value : <Minus />}</span>
    </div>
  );
};

type PageTypeWithDescriptionProps = {
  title: string;
  description: string;
  isSelected: boolean;
  className?: string;
};

const PageTypeWithDescription: React.FC<PageTypeWithDescriptionProps> = ({
  title,
  description,
  isSelected,
  className,
}) => {
  return (
    <div
      className={twMerge(
        className,
        "flex w-full flex-col rounded-sm p-1.5",
        isSelected && "bg-selectable-selected",
        !isSelected && "hover:bg-selectable-hover",
      )}
    >
      <p className="header-h5 text-default">{title}</p>
      <p className="text-muted text-sm">{description}</p>
    </div>
  );
};

type ExperimentPageTypeSelectProps = {
  pageType: PageTypeEnum;
  isFractional: boolean;
  isV2: boolean;
  onChange: (pageType: PageTypeEnum, isFractional: boolean) => void;
};

const ExperimentPageTypeSelect: React.FC<ExperimentPageTypeSelectProps> = ({
  pageType,
  isFractional,
  isV2,
  onChange,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const handleOpenChange = () => {
    setIsOpen(!isOpen);
  };

  // Create a combined value to represent the current selection
  const currentValue = isFractional ? "fractional" : pageType;

  const options = [
    {
      displayValue: "All Viewed Pages",
      label: (
        <PageTypeWithDescription
          title="All Viewed Pages"
          description="Data for any page visited within the session"
          isSelected={pageType === PageTypeEnum.ALL_PAGES && !isFractional}
        />
      ),
      value: PageTypeEnum.ALL_PAGES,
      isDisabled: false,
    },
    {
      displayValue: "Entry Pages",
      label: (
        <PageTypeWithDescription
          title="Entry Pages"
          description="Shows metrics for pages that began the session"
          isSelected={pageType === PageTypeEnum.ENTRY_PAGES && !isFractional}
        />
      ),
      value: PageTypeEnum.ENTRY_PAGES,
      isDisabled: false,
    },
    ...(isV2
      ? [
          {
            displayValue: "Fractional Attribution",
            label: (
              <PageTypeWithDescription
                title="Fractional Attribution"
                description="Uses fractional attribution model for conversion tracking"
                isSelected={isFractional}
              />
            ),
            value: "fractional" as const,
            isDisabled: false,
          },
        ]
      : []),
  ];

  const handleChange = (newValue: string) => {
    if (newValue === "fractional") {
      onChange(PageTypeEnum.ENTRY_PAGES, true);
    } else {
      onChange(newValue as PageTypeEnum, false);
    }
  };

  return (
    <Combobox.Root
      options={options}
      open={isOpen}
      onOpenChange={handleOpenChange}
      value={currentValue}
      onChange={handleChange}
    >
      <Combobox.Trigger>
        <Combobox.SelectionButton
          size="base"
          title={
            options.find((option) => option.value === currentValue)
              ?.displayValue ?? ""
          }
          isPlaceholder={false}
        />
      </Combobox.Trigger>
      <Combobox.Popover side="bottom" align="end" layoutClassName="w-[300px]">
        <Combobox.Content size="lg" />
      </Combobox.Popover>
    </Combobox.Root>
  );
};

/**
 * Normalizes URL paths to match how they are stored in the analytics database.
 * The backend (transformStoredEventToV2 in analytics-database/src/utils.ts) strips
 * trailing slashes from paths before storing them, so "/" becomes "".
 * This ensures our filters match the actual stored data.
 *
 * @author Ryan Voris 2025-06-26
 */
const normalizeUrlPath = (path: string): string => {
  return path.replace(/\/$/, "");
};

const useAnalyticsReadForExperiments = (
  experimentId: string,
  startDatetime: number,
  endDatetime: number,
  interval: number,
  variationTargetUrlHosts: string[],
  variationPagePaths: string[],
  pageType: PageTypeEnum,
  isFractional: boolean,
) => {
  /**
   * NOTE (Max, 2024-10-17): We use a basic query where we get:
   * 1) urlHosts from the experiment variation urls
   * 2) mainRange datetimes from the Experiment's createdAt / endedAt dates
   * 3) filters using the experimentId: we're only interested in CH rows that contain
   * the experimentId as a urlParam
   * 4) urlPath filters from the experiment variation urls to narrow down to specific pages
   * 5) pageType filter to determine whether to show all pages or entry pages only
   * 6) isFractional flag to determine whether to use fractional attribution
   */
  const query: AnalyticsQuery = React.useMemo(
    () => ({
      ...DEFAULT_QUERY,
      urlHosts: variationTargetUrlHosts,
      order: "DESC",
      sortMetric: "unique_sessions",
      ranges: {
        mainRange: {
          id: DEFAULT_MAIN_RANGE_ID,
          startDatetime: startDatetime,
          endDatetime: endDatetime,
          interval,
        },
        compareAtRanges: [],
      },
      filters: {
        ...DEFAULT_FILTERS,
        pageType,
        urlPath: [
          {
            operator: ConditionOperatorEnum.EQUALS,
            value: variationPagePaths,
          },
        ],
        urlParams: {
          "replo-experiment-id": [
            {
              operator: ConditionOperatorEnum.EQUALS,
              value: [`${experimentId}`],
            },
          ],
        },
      },
      mode: "groupByUrlPath",
      isFractional,
    }),
    [
      experimentId,
      startDatetime,
      endDatetime,
      interval,
      variationTargetUrlHosts,
      variationPagePaths,
      pageType,
      isFractional,
    ],
  );

  return useBasicAnalyticsRead({
    type: "withoutUrlSyncQuery",
    queryConfig: query,
  });
};

const ExperimentResultsTabLinkInfo: React.FC<{
  experiment: Experiment;
}> = ({ experiment }) => {
  const logEvent = useLogAnalytics();
  const { subscriptionInfo } = useSubscriptionInfo();
  const subscriptionTier = subscriptionInfo?.tier || BillingTiers.FREE;

  const {
    links: { data: links },
  } = useExperimentApi();

  const getSelectedLinkUrl = (): string => {
    const link = links?.find(
      (link) =>
        link.id ===
        (experiment.completedAnalyticsLinkId ?? experiment.analyticsLinkId),
    );
    return link?.url ?? "";
  };

  return (
    <div className="bg-sheet border-0.5 border-border flex flex-row items-center justify-between rounded-md px-4 py-3">
      <div className="flex flex-row gap-1.5">
        <Network size={16} />
        <div className="flex flex-col">
          <span className="text-default text-sm font-medium">
            Direct traffic to your Test Link
          </span>
          <span className="text-placeholder text-sm">
            Only traffic directed through this test link will be included in the
            test.
          </span>
        </div>
      </div>
      <div className="flex flex-row gap-2.5">
        <Button
          variant="tertiary"
          size="base"
          endEnhancer={<ExternalLink size={14} />}
          onClick={() => {
            const url = getSelectedLinkUrl();
            if (url) {
              window.open(url, "_blank");
              logEvent("experiment.link.preview", {
                billingPlanTier: subscriptionTier,
              });
            }
          }}
        >
          <span className="text-default text-sm font-medium">Preview</span>
        </Button>
        <Button
          variant="primary"
          size="base"
          endEnhancer={<Copy size={14} />}
          onClick={() => {
            const url = getSelectedLinkUrl();
            if (url) {
              copy(url);
              successToast(
                "Link Copied",
                "The test link has been copied to your clipboard.",
              );
              logEvent("experiment.link.copy", {
                billingPlanTier: subscriptionTier,
              });
            }
          }}
        >
          <span className="text-sm font-medium text-white">Copy link</span>
        </Button>
      </div>
    </div>
  );
};

const StatSigInfoText: React.FC = () => {
  return (
    <div className="flex flex-row items-center justify-end gap-1">
      <span className="text-muted text-sm">
        Want to see if you've reached stat sig?
      </span>
      <Button
        variant="tertiary"
        size="sm"
        endEnhancer={<ArrowUpRight size={12} className="text-primary" />}
        to={docs.abTesting.education.statSigCalculator}
        target="_blank"
      >
        <span className="text-primary text-sm font-medium">
          Use our calculator
        </span>
      </Button>
    </div>
  );
};

const ExperimentResultsTabInner: React.FC<{
  simpleExperiment: SimpleExperiment;
  workspaceId: string | undefined;
  experiment: Experiment | null;
}> = ({ simpleExperiment, experiment, workspaceId }) => {
  const { dispatchExperimentEdit, handleReactHookFormSubmit } =
    useExperimentEdit();
  const projectId = useCurrentProjectId();

  const navigate = useNavigate();

  const isV2 = isFeatureEnabled("analytics-v2-data-model");

  const [pageType, setPageType] = React.useState<PageTypeEnum>(
    PageTypeEnum.ENTRY_PAGES,
  );
  const [isFractional, setIsFractional] = React.useState(isV2);

  const {
    id,
    createdAt,
    createdAtHuman,
    endedAt,
    endedAtHuman,
    variations,
    name,
  } = simpleExperiment;

  const winner = variations.find(
    (variation) =>
      experiment?.selectedDisplayWinnerVariationId === variation.id,
  );

  const startDatetime = startOfDay(createdAt).valueOf();
  const endDatetime = endOfDay(endedAt ?? Date.now()).valueOf() + 1;
  const interval = endDatetime - startDatetime;
  // NOTE Ben Vogt 2025-03-13: We only need to query for analytics data
  // taking place on the url hosts in the experiment variations target URLs.
  const urlHostsInExperiment = [
    ...new Set(
      simpleExperiment.variations.map(({ target }) => new URL(target!).host),
    ),
  ];

  const variationTargetPagePaths = simpleExperiment.variations
    .filter((variation) => variation.target !== null)
    .map((variation) => normalizeUrlPath(new URL(variation.target!).pathname));

  const durationDays = convertMsToDays(interval);

  const { mainRangeResults, isLoading } = useAnalyticsReadForExperiments(
    id,
    startDatetime,
    endDatetime,
    interval,
    urlHostsInExperiment,
    variationTargetPagePaths,
    pageType,
    isFractional,
  );

  const totalSessions = mainRangeResults
    ? mainRangeResults.reduce((total, rangeResult) => {
        const sessions = rangeResult.metrics.unique_sessions?.[0] || 0;
        return total + sessions;
      }, 0)
    : 0;

  const experimentResults = variations
    .filter((variation) => variation.target !== null)
    .map((variation) => {
      const targetURL = new URL(variation.target!);
      const urlPath = normalizeUrlPath(targetURL.pathname);

      const variationSlug = variation.slug;

      const associatedRangeResult: RangeResult | null =
        mainRangeResults?.find(
          (rangeResult) => rangeResult.urlPath === urlPath,
        ) ?? null;

      const metrics = associatedRangeResult?.metrics ?? {};

      return {
        targetURL,
        variationSlug,
        metrics,
      };
    });

  const getWinnerDisplay = () => {
    if (!endedAt) {
      return null;
    }

    return winner?.slug ?? "No Winner";
  };

  if (!experiment || !workspaceId) {
    return null;
  }

  const experimentsListPath = Boolean(projectId)
    ? generatePath(routes.shops.experiments.list, {
        projectId,
      })
    : generatePath(routes.workspace.experiments.list, {
        workspaceId,
      });

  return (
    <ExperimentsLayout
      headerTitleComponent={
        <div className="flex flex-row items-center gap-1">
          <ExperimentEditableHeader
            title={name}
            experimentId={id}
            dispatchExperimentEdit={dispatchExperimentEdit}
            handleReactHookFormSubmit={handleReactHookFormSubmit}
            isLoadingRequiredData={!workspaceId}
          />
          {experiment && (
            <Tag
              variant={
                experimentStatusMapping[getExperimentStatus(experiment)].variant
              }
              size="base"
            >
              {experimentStatusMapping[getExperimentStatus(experiment)].label}
            </Tag>
          )}
        </div>
      }
      headerActions={<ExperimentActionsBar experiment={experiment} />}
      onBackClick={() => {
        navigate(experimentsListPath);
      }}
    >
      <ExperimentTabWrapper>
        <div className="flex flex-col gap-4">
          <ExperimentResultsTabLinkInfo experiment={experiment} />
          <div className="grid grid-cols-12 gap-4">
            <ValuesContainer className="col-span-6">
              <TitleWithValue
                title="Sessions"
                value={formatWithCommasAndTwoDecimals(totalSessions, false)}
              />
              <TitleWithValue
                title="Duration"
                value={`${durationDays} day${durationDays !== 1 ? "s" : ""}`}
              />
              <TitleWithValue title="Winner" value={getWinnerDisplay()} />
            </ValuesContainer>

            <ValuesContainer className="col-span-6">
              <TitleWithValue title="Start Date" value={createdAtHuman} />
              <TitleWithValue title="End Date" value={endedAtHuman} />
            </ValuesContainer>
          </div>
          <div className="flex flex-row items-center justify-end">
            <ExperimentPageTypeSelect
              pageType={pageType}
              isFractional={isFractional}
              isV2={isV2}
              onChange={(newPageType, newIsFractional) => {
                setPageType(newPageType);
                setIsFractional(newIsFractional);
              }}
            />
          </div>
          {!isLoading ? (
            <div className="flex flex-col gap-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <CustomTableHead
                      label="Variant name"
                      className="w-[300px]"
                    />
                    {OVERVIEW_TABLE_METRICS.map((metric) => (
                      <CustomTableHead
                        key={metric.key}
                        label={metric.label}
                        tooltip={metric.tooltip}
                        className={twMerge("w-[150px]")}
                      />
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {experimentResults?.map((experimentResult, index) => {
                    return (
                      <ExperimentResultsTableRow
                        key={experimentResult.variationSlug}
                        experimentResult={experimentResult}
                        index={index}
                        isFractional={isFractional}
                      />
                    );
                  })}
                </TableBody>
              </Table>
              <StatSigInfoText />
            </div>
          ) : (
            <Loader />
          )}
        </div>
      </ExperimentTabWrapper>
    </ExperimentsLayout>
  );
};

const ExperimentResultsTab: React.FC = () => {
  const { simpleExperiment, workspaceId, experiment } = useExperimentDetails();

  if (!experiment) {
    return null;
  }

  if (!simpleExperiment || !workspaceId) {
    return <Loader />;
  }
  return (
    <ExperimentResultsTabInner
      simpleExperiment={simpleExperiment}
      workspaceId={workspaceId}
      experiment={experiment}
    />
  );
};

type ExperimentResult = {
  targetURL: URL;
  variationSlug: string;
  metrics: RangeResult["metrics"];
};

type ExperimentResultsTableRowProps = {
  experimentResult: ExperimentResult;
  index: number;
  isFractional: boolean;
};

const ExperimentResultsTableRow: React.FC<ExperimentResultsTableRowProps> = ({
  experimentResult,
  index,
  isFractional,
}) => {
  const { targetURL, variationSlug, metrics } = experimentResult;

  return (
    <TableRow>
      <TableCell>
        <div className="grid grid-cols-[auto,1fr] gap-x-2 gap-y-1">
          <div className="flex flex-row items-center gap-2">
            <VariationNameStartEnhancer index={index} size="sm" />
            <Tooltip triggerAsChild content={targetURL.toString()} delay={800}>
              <span className="text font-medium">{variationSlug}</span>
            </Tooltip>
          </div>
          <div />
          <div className="flex items-center gap-1 truncate">
            <Tooltip triggerAsChild content={targetURL.toString()} delay={800}>
              <span className="text-muted truncate text-sm font-medium">
                {targetURL.toString()}
              </span>
            </Tooltip>
            <Link
              to={targetURL.toString()}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink size={12} />
            </Link>
          </div>
        </div>
      </TableCell>
      {OVERVIEW_TABLE_METRICS.map((metric) => {
        const currentMetricValues = metrics[metric.key] ?? [];
        const value = currentMetricValues[0] ?? null;

        return (
          <TableCell key={metric.key}>
            <MetricWithDelta
              name={metric.key}
              value={value}
              doesCompareValueExist={false}
              wrapperClassName="justify-start flex-grow"
              valueClassName="text-sm"
              touchType="all"
              isFractional={isFractional}
            />
          </TableCell>
        );
      })}
    </TableRow>
  );
};

type CustomTableHeadProps = {
  label: string;
  tooltip?: string;
  className?: string;
};

const CustomTableHead = ({
  label,
  tooltip,
  className,
}: CustomTableHeadProps) => {
  return (
    <TableHead className={className}>
      <div className="flex items-center gap-2">
        {tooltip ? (
          <Tooltip content={tooltip} side="top" triggerAsChild>
            <span
              className={twMerge(
                "truncate",
                "border-placeholder border-b-[1px] border-dashed",
              )}
            >
              {label}
            </span>
          </Tooltip>
        ) : (
          <span className="truncate">{label}</span>
        )}
      </div>
    </TableHead>
  );
};

export default ExperimentResultsTab;
